import { useEffect, useRef } from "react";
import { trackPageView } from "@/lib/page-views.functions";

const SESSION_KEY = "sa.session.id";
const REFERRER_KEY = "sa.session.ref";
const LANDING_KEY = "sa.session.landing";

/**
 * Hosts that indicate the visitor is inside the Lovable editor or a preview
 * URL, not real production traffic. We stop capturing page views entirely
 * on those hosts so preview clicks never pollute analytics.
 */
function isPreviewHost(host: string): boolean {
  return (
    host.endsWith(".lovable.dev") ||
    host.endsWith(".lovableproject.com") ||
    host.endsWith(".lovable.app") && host.includes("preview") ||
    host === "localhost" ||
    host.startsWith("127.")
  );
}

function getSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id =
        (crypto?.randomUUID?.() ??
          Math.random().toString(36).slice(2) + Date.now().toString(36)).slice(0, 36);
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

const VISITOR_KEY = "sa.visitor.id";

/**
 * Long-lived, anonymous per-browser marker. Unlike the session id (which dies
 * with the tab) this survives across days, which is the only way to tell a
 * returning visitor from a new one. No personal data is stored.
 */
function getVisitorId(): string {
  if (typeof window === "undefined") return "";
  try {
    let id = window.localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = (crypto?.randomUUID?.() ??
        Math.random().toString(36).slice(2) + Date.now().toString(36)).slice(0, 36);
      window.localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

/**
 * Normalize referrer at capture so dashboards don't have to reconcile
 * `l.instagram.com`, `instagram.com`, `lm.instagram.com`, and Instagram
 * in-app browser UAs as separate sources.
 *
 * Priority: explicit UTM > document.referrer host > IG in-app UA fallback.
 * Format: `<canonical-source>` OR `utm:<source>[:<medium>[:<campaign>]]`.
 */
function classifyHost(host: string): string {
  const h = host.replace(/^www\./, "");
  if (h.includes("instagram")) return "instagram";
  if (h.includes("facebook") || h === "fb.com" || h === "lm.facebook.com") return "facebook";
  if (h.includes("t.co") || h.includes("twitter") || h.includes("x.com")) return "twitter";
  if (h.includes("tiktok")) return "tiktok";
  if (h.includes("youtube") || h.includes("youtu.be")) return "youtube";
  if (h.includes("reddit")) return "reddit";
  if (h.includes("google")) return "google";
  if (h.includes("bing")) return "bing";
  if (h.includes("duckduckgo")) return "duckduckgo";
  if (h.includes("ecosia")) return "ecosia";
  if (h.includes("chatgpt") || h.includes("openai")) return "chatgpt";
  if (h.includes("perplexity")) return "perplexity";
  if (h.includes("linkedin")) return "linkedin";
  if (h.includes("threads")) return "threads";
  return h;
}

function detectInAppReferrer(ua: string): string | null {
  const u = ua.toLowerCase();
  // Meta in-app browsers set FBAN/FBAV (FB) or Instagram in the UA.
  if (u.includes("instagram")) return "instagram";
  if (u.includes("fban") || u.includes("fbav") || u.includes("fb_iab")) return "facebook";
  if (u.includes("tiktok") || u.includes("bytedance")) return "tiktok";
  if (u.includes("threads")) return "threads";
  if (u.includes("linkedinapp")) return "linkedin";
  return null;
}

function computeReferrer(): string {
  if (typeof window === "undefined") return "";
  try {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get("utm_source");
    const utmMedium = params.get("utm_medium");
    const utmCampaign = params.get("utm_campaign");
    if (utmSource) {
      // Encode all UTM fields so attribution keeps campaign granularity.
      const parts = [utmSource, utmMedium, utmCampaign]
        .map((v) => (v ?? "").toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 40))
        .filter(Boolean);
      return `utm:${parts.join(":")}`;
    }
    const raw = document.referrer || "";
    if (raw) {
      try {
        const u = new URL(raw);
        if (u.host !== window.location.host) return classifyHost(u.host);
      } catch {
        /* ignore */
      }
    }
    // Fallback: identify Meta/TikTok in-app browsers by user agent (referrer is
    // often stripped in these). This is how we recover most IG traffic.
    const inApp = detectInAppReferrer(navigator.userAgent || "");
    if (inApp) return `inapp:${inApp}`;
    return "";
  } catch {
    return "";
  }
}

function getReferrer(): string {
  if (typeof window === "undefined") return "";
  try {
    const stored = window.sessionStorage.getItem(REFERRER_KEY);
    if (stored !== null) return stored;
    const ref = computeReferrer();
    window.sessionStorage.setItem(REFERRER_KEY, ref);
    return ref;
  } catch {
    return "";
  }
}

/** Landing page for this session - captured once, referenced by exit tracking later. */
function ensureLanding() {
  if (typeof window === "undefined") return;
  try {
    if (!window.sessionStorage.getItem(LANDING_KEY)) {
      window.sessionStorage.setItem(LANDING_KEY, window.location.pathname);
    }
  } catch {
    /* ignore */
  }
}

/**
 * JS-executing crawlers (Google/Bing renderers, AI crawlers, link previewers,
 * uptime monitors, headless QA browsers) fire our tracker exactly once and
 * never scroll. Left unflagged they inflate "sessions" and crush
 * pages/session, which is what made the Aug 2026 numbers look like a traffic
 * collapse. Flag them as internal so every dashboard excludes them.
 */
function isBotAgent(ua: string): boolean {
  return /bot|crawl|spider|slurp|headless|phantom|puppeteer|playwright|lighthouse|pagespeed|monitor|uptime|preview|fetch|scrapy|curl|wget|python-requests|axios|go-http|node-fetch|facebookexternalhit|embedly|quora link|whatsapp|telegram|discord|skype|semrush|ahrefs|mj12|dotbot|petal|bytespider|gptbot|claudebot|perplexity|ccbot|amazonbot|applebot|dataforseo/i.test(ua);
}

function shouldTrack(): boolean {
  if (typeof window === "undefined") return false;
  return !isPreviewHost(window.location.hostname);
}


/**
 * Read once per page load: is this browser flagged as an internal (QA / founder)
 * session? Toggled via markBrowserInternal() from the admin UI. Cheap synchronous
 * localStorage read; we cache in module scope so we don't re-read on every event.
 */
let _internalCached: boolean | null = null;
export function isInternalSession(): boolean {
  if (typeof window === "undefined") return false;
  if (_internalCached !== null) return _internalCached;
  try {
    _internalCached = window.localStorage.getItem("sa.internal") === "1";
  } catch { _internalCached = false; }
  return _internalCached;
}
export function markBrowserInternal(on: boolean) {
  if (typeof window === "undefined") return;
  try {
    if (on) {
      window.localStorage.setItem("sa.internal", "1");
      window.localStorage.setItem("plausible_ignore", "true");
    } else {
      window.localStorage.removeItem("sa.internal");
      window.localStorage.removeItem("plausible_ignore");
    }
    _internalCached = on;
  } catch { /* ignore */ }
}

/** Record a single page view per (path,kind) for this session. */
export function usePageView(kind: string) {
  const fired = useRef<string | null>(null);
  useEffect(() => {
    if (!shouldTrack()) return;
    const path = window.location.pathname;
    const key = `${kind}:${path}`;
    if (fired.current === key) return;
    fired.current = key;
    ensureLanding();
    try {
      trackPageView({
        data: {
          path,
          kind,
          referrer: getReferrer() || undefined,
          session_id: getSessionId() || undefined,
          visitor_id: getVisitorId() || undefined,
          is_internal: isInternalSession() || undefined,
        },
      }).catch(() => {});
    } catch {
      /* never break UI */
    }
  }, [kind]);
}

/**
 * Fire a semantic event. `kind` is a short snake_case verb: `hero_click`,
 * `share_click`, `save_click`, `audio_start`, `audio_complete`,
 * `search_query`, `newsletter_view`, `journey_step`, `quiz_complete`,
 * `read_25`, `read_50`, `read_75`, `read_100`, etc.
 */
export function trackConversion(kind: string, path?: string) {
  if (!shouldTrack()) return;
  try {
    trackPageView({
      data: {
        path: path ?? window.location.pathname,
        kind,
        referrer: getReferrer() || undefined,
        session_id: getSessionId() || undefined,
        visitor_id: getVisitorId() || undefined,
        is_internal: isInternalSession() || undefined,
      },
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

/** Fire a conversion event at most once per session per key. */
export function trackConversionOnce(key: string, kind: string, path?: string) {
  if (typeof window === "undefined") return;
  try {
    const k = `sa.conv.${key}`;
    if (window.sessionStorage.getItem(k)) return;
    window.sessionStorage.setItem(k, "1");
    trackConversion(kind, path);
  } catch {
    trackConversion(kind, path);
  }
}
